import { debounce as lodashDebounce, get as lodashGet } from "lodash";
import { createElement, type ReactElement, useMemo } from "react";
import { renderToString } from "react-dom/server";
import type { GroupBase, OptionsOrGroups, StylesConfig } from "react-select";
import type { CreatableProps } from "react-select/creatable";
import Creatable from "react-select/creatable";
import {
  type ComponentProps,
  type UseAsyncPaginateParams,
  withAsyncPaginate,
} from "react-select-async-paginate";
import {
  ElementFactory,
  ExpressionValidator,
  Question,
  type QuestionDropdownModel,
  Serializer,
  settings,
  type SurveyModel,
} from "survey-core";
import { type SurveyCreatorModel } from "survey-creator-core";
import { type SurveyCreator } from "survey-creator-react";
import { ReactQuestionFactory } from "survey-react-ui";
import { apiRequestContractHandler } from "shared/api/apiRequestContractHandler";
import { getSocietyChoicesContract } from "shared/api/contracts/society/societyId/choices";
import { getSocietyChoicesQuestionNameContract } from "shared/api/contracts/society/societyId/choices/questionName";
import { getCustomReportIdContract } from "shared/api/contracts/society/societyId/reports/reportId";
import { removeHTMLTags } from "shared/components/survey-js-form/utils/removeHTMLTags";
import TemplateComponent from "shared/components/template/TemplateComponent";
import { type SessionView } from "shared/mappers/database/session/session";
import { type SocietyCustomChoiceDto } from "shared/mappers/database/society-custom-choices/societyCustomChoice";

type AsyncPaginateCreatableProps<
  OptionType,
  Group extends GroupBase<OptionType>,
  Additional,
  IsMulti extends boolean,
> = CreatableProps<OptionType, IsMulti, Group> &
  UseAsyncPaginateParams<OptionType, Group, Additional> &
  ComponentProps<OptionType, Group, IsMulti>;

type AsyncPaginateCreatableType = <
  OptionType,
  Group extends GroupBase<OptionType>,
  Additional,
  IsMulti extends boolean = false,
>(
  props: AsyncPaginateCreatableProps<OptionType, Group, Additional, IsMulti>,
) => ReactElement;

export const CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN = "pillar_choice_dropdown";

// Sentinel choice inserted at the top of the "Pillar Choice Category" dropdown.
// Selecting it flags the question as a custom-report-backed dropdown, which
// reveals the "Custom Report ID" property (and its dependent settings).
export const PILLAR_CHOICE_CUSTOM_REPORT_VALUE = "--- CUSTOM REPORT ---";

export class PillarChoiceDropdownModel extends Question {
  session?: SessionView;
  linkedValueSyncAttached = false;
  /**
   * Whether the value the question is holding was written by the lookup below,
   * rather than loaded with the form or picked by whoever is filling it in.
   * `doNotChangeValue` reads it to tell "already answered" from "answered by
   * me a moment ago" - see the guard at the top of `syncValue`.
   */
  valueSetByLookup = false;
  /**
   * The watched answers the last lookup ran for, so an unrelated change does
   * not re-run it. This lives on the question rather than in a survey variable:
   * setting a variable re-runs every condition in the form and re-renders it,
   * and a re-render overwrites each text input from its question's value -
   * throwing away whatever someone is part way through typing elsewhere.
   */
  lookupSnapshot?: string;

  getType() {
    return CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN;
  }

  onSurveyLoad(): void {
    super.onSurveyLoad();

    if (this.linkedValueSyncAttached) {
      return;
    }

    const survey = this.survey as SurveyModel | undefined;
    const linkQuestionValues = this.getPropertyValue("link_question_values") as
      | string
      | undefined;
    const customReportFilters = this.getPropertyValue(
      "custom_report_filters",
    ) as CustomReportFilterRow[] | undefined;

    const linkedName = linkQuestionValues
      ? removeHTMLTags(linkQuestionValues)
      : undefined;
    const watchedNames = new Set<string>();
    if (linkedName) {
      watchedNames.add(linkedName);
    }
    for (const row of customReportFilters ?? []) {
      if (row?.questionName) {
        watchedNames.add(row.questionName);
      }
    }

    if (!survey || watchedNames.size === 0) {
      return;
    }

    const watchedRoots = new Set(
      [...watchedNames].map((name) => name.split(".")[0]),
    );

    const clearLookupValue = () => {
      this.lookupSnapshot = undefined;
      if (!this.isEmpty()) {
        this.value = undefined;
      }
    };

    const syncValue = lodashDebounce(async (sender: SurveyModel) => {
      // `doNotChangeValue` stops the lookup replacing an answer that is not
      // its own: one loaded with the form, or one whoever is filling it in
      // picked by hand. A value the lookup itself wrote is still fair game, so
      // a postal code typed a few digits at a time keeps refining what it
      // resolves to right up until the member takes the question over. Bailing
      // out here also spares the request, which could only ever have produced
      // a value that gets discarded.
      if (
        this.getPropertyValue("doNotChangeValue") &&
        !this.isEmpty() &&
        !this.valueSetByLookup
      ) {
        return;
      }

      const allValues = sender.data;
      const linkedValue = linkedName
        ? lodashGet(allValues, linkedName)
        : undefined;

      if (linkedName && !linkedValue) {
        clearLookupValue();
        return;
      }

      const filterParams = buildCustomReportFilterParams(
        customReportFilters,
        sender,
      );

      if (!linkedName && Object.keys(filterParams).length === 0) {
        clearLookupValue();
        return;
      }

      const snapshot = JSON.stringify(
        [...watchedNames]
          .sort()
          .map((name) => [name, lodashGet(allValues, name) ?? null]),
      );

      if (this.lookupSnapshot === snapshot) {
        return;
      }

      const options = await fetchPillarChoiceOptions({
        societyId: this.session?.societyId ?? 0,
        questionName: this.getPropertyValue(
          "pillar_choices_question_name",
        ) as string,
        distinctSearchKey: this.getPropertyValue("distinct_search_key") as
          | string
          | undefined,
        inputValue: linkedName ? String(linkedValue) : "",
        questionValue: this.getPropertyValue("question_value") as
          | string
          | undefined,
        customReportId: this.getPropertyValue("custom_report_id") as
          | number
          | undefined,
        customReportLabelPath: this.getPropertyValue(
          "custom_report_label_path",
        ) as string | undefined,
        customReportValuePath: this.getPropertyValue(
          "custom_report_value_path",
        ) as string | undefined,
        customReportFilterParams: filterParams,
      });

      this.lookupSnapshot = snapshot;

      // Assigning the same value again would still re-render the form, so only
      // an answer that actually moved is written back.
      const label = options[0]?.label;
      if (this.value !== label) {
        this.valueSetByLookup = true;
        this.value = label;
      }
    }, 500);

    survey.onValueChanged.add(
      (sender: SurveyModel, options: { name?: string }) => {
        if (!options?.name || !watchedRoots.has(options.name)) {
          return;
        }
        void syncValue(sender);
      },
    );

    this.linkedValueSyncAttached = true;
    void syncValue(survey);
  }
}

(settings as { customIcons: { [index: string]: string } }).customIcons[
  "icon-" + CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN
] = "icon-dropdown";

export type PillarChoiceDropdownInputProps = {
  creator: SurveyCreator;
  isDisplayMode: boolean;
  question: PillarChoiceDropdownModel;
  session: SessionView;
};

export type OptionsValuesPillarChoice = {
  value: number | string;
  label: string;
};

type FetchPillarChoiceOptionsArgs = {
  societyId: number;
  questionName: string;
  distinctSearchKey?: string;
  inputValue: string;
  questionValue?: string;
  customReportId?: number;
  customReportLabelPath?: string;
  customReportValuePath?: string;
  customReportFilterParams?: Record<string, string>;
};

export type CustomReportFilterRow = {
  filterKey?: string;
  questionName?: string;
};

const buildCustomReportFilterParams = (
  filters: CustomReportFilterRow[] | undefined,
  survey: SurveyModel | undefined,
  doNotSendIfClick?: boolean,
  isClickTrigger?: boolean,
): Record<string, string> => {
  if (!filters?.length || !survey) {
    return {};
  }

  if (doNotSendIfClick && isClickTrigger) {
    return {};
  }

  const params: Record<string, string> = {};
  const allValues = survey.data;
  for (const row of filters) {
    if (!row?.filterKey || !row?.questionName) {
      continue;
    }
    const raw = lodashGet(allValues, row.questionName);
    if (raw === undefined || raw === null || raw === "") {
      continue;
    }
    params[row.filterKey] = String(raw);
  }
  return params;
};

const mapChoicesToOptions = (
  results: SocietyCustomChoiceDto[],
  questionValue?: string,
): OptionsValuesPillarChoice[] =>
  results.map((choice) => {
    const value = questionValue
      ? choice[questionValue as keyof SocietyCustomChoiceDto]
      : choice.customQuestionChoiceId;

    return {
      value: String(value),
      label: choice.text,
    };
  });

const mapReportRowsToOptions = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows: any[],
  labelPath?: string,
  valuePath?: string,
): OptionsValuesPillarChoice[] =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows.map((row: any) => {
    const value = valuePath ? lodashGet(row, valuePath) : undefined;
    const label = labelPath ? lodashGet(row, labelPath) : undefined;
    return {
      value: String(value ?? ""),
      label: String(label ?? ""),
    };
  });

const fetchPillarChoiceOptions = async ({
  societyId,
  questionName,
  distinctSearchKey,
  inputValue,
  questionValue,
  customReportId,
  customReportLabelPath,
  customReportValuePath,
  customReportFilterParams,
}: FetchPillarChoiceOptionsArgs): Promise<OptionsValuesPillarChoice[]> => {
  // Without a custom report or a choice category there is nothing to query;
  // hitting the choices endpoint with an undefined questionName produces a
  // `/choices/[questionName]` request that always 404s.
  if (!customReportId && !questionName) {
    return [];
  }

  if (customReportId) {
    const reportResponse = await apiRequestContractHandler(
      getCustomReportIdContract,
      { params: { societyId, reportId: customReportId } },
      { text: inputValue, ...(customReportFilterParams ?? {}) },
      { page: 1, pageSize: 25 },
    );
    return mapReportRowsToOptions(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      reportResponse.results as any[],
      customReportLabelPath,
      customReportValuePath,
    );
  }

  const choicesResponse = await apiRequestContractHandler(
    getSocietyChoicesQuestionNameContract,
    {
      params: {
        societyId,
        questionName,
        ...(distinctSearchKey !== undefined && {
          distinctOn: distinctSearchKey,
        }),
      },
    },
    { text: inputValue },
    {
      page: 1,
      pageSize: 25,
    },
  );

  return mapChoicesToOptions(choicesResponse.results, questionValue);
};

type PillarChoiceCategoryOption = { value: string; text: string };

const customReportCategoryOption: PillarChoiceCategoryOption = {
  value: PILLAR_CHOICE_CUSTOM_REPORT_VALUE,
  text: PILLAR_CHOICE_CUSTOM_REPORT_VALUE,
};

/**
 * Loads the society's choice categories for the survey creator's "Pillar Choice
 * Category" property dropdown. The custom report option is always pinned to the
 * top, even if the request fails, so the report configuration stays reachable.
 */
const fetchPillarChoiceCategoryOptions = async (
  societyId: number,
): Promise<PillarChoiceCategoryOption[]> => {
  const options: PillarChoiceCategoryOption[] = [customReportCategoryOption];

  try {
    const response = await apiRequestContractHandler(
      getSocietyChoicesContract,
      { params: { societyId } },
      undefined,
      { page: 1, pageSize: 1000 },
    );

    for (const question of response.results) {
      if (question.questionName) {
        options.push({
          value: question.questionName,
          text: question.questionName,
        });
      }
    }
  } catch {
    // Keep the custom report option available if the request fails.
  }

  return options;
};

/**
 * Defers loading the choice categories until the admin actually opens the
 * "Pillar Choice Category" dropdown in the Property Grid, rather than firing a
 * request for every pillar choice question that gets selected on the design
 * surface.
 */
const wirePillarChoiceCategoryLazyLoad = (
  creator: SurveyCreatorModel,
  session: SessionView,
) => {
  const wiredFlag = "_pillarChoiceCategoryLazyLoadWired";
  const creatorRecord = creator as unknown as Record<string, unknown>;
  if (creatorRecord[wiredFlag]) {
    return;
  }
  creatorRecord[wiredFlag] = true;

  creator.onPropertyGridSurveyCreated.add((_sender, options) => {
    if (options.obj?.getType?.() !== CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN) {
      return;
    }

    const editor = options.survey.getQuestionByName(
      "pillar_choices_question_name",
    ) as QuestionDropdownModel | null;
    if (!editor) {
      return;
    }

    let loading = false;
    editor.onOpened.add(() => {
      if (loading) {
        return;
      }
      loading = true;
      void (async () => {
        try {
          editor.choices = await fetchPillarChoiceCategoryOptions(
            session.societyId ?? 0,
          );
        } finally {
          loading = false;
        }
      })();
    });
  });
};

export const PillarChoiceDropdown = (props: PillarChoiceDropdownInputProps) => {
  const {
    pillar_choices_question_name,
    multi_select,
    multi_select_duplicate,
    doNotSendIfClick,
    placeholder_text,
    other_option,
    question_value,
    handlebars_template,
    distinct_search_key,
    min_choices,
    max_choices,
    custom_report_id,
    custom_report_label_path,
    custom_report_value_path,
    custom_report_filters,
  } = props.question;

  const survey = props.question.survey as SurveyModel;

  const fetchCustomChoices = async (
    inputValue: string,
    loadedOptions: OptionsOrGroups<
      OptionsValuesPillarChoice,
      GroupBase<OptionsValuesPillarChoice>
    >,
    page = 1,
  ) => {
    const pageSize = 25;

    // Nothing to fetch until the question is pointed at a choice category or a
    // custom report; otherwise we would call `/choices/[questionName]` with an
    // undefined questionName.
    if (!custom_report_id && !pillar_choices_question_name) {
      return { options: [], hasMore: false, additional: page + 1 };
    }

    if (custom_report_id) {
      const isClickTrigger = inputValue === "";
      const extraFilters = buildCustomReportFilterParams(
        custom_report_filters as CustomReportFilterRow[] | undefined,
        survey,
        doNotSendIfClick,
        isClickTrigger,
      );
      const reportResponse = await apiRequestContractHandler(
        getCustomReportIdContract,
        {
          params: {
            societyId: props.session.societyId ?? 0,
            reportId: custom_report_id,
          },
        },
        { text: inputValue, ...extraFilters },
        { page, pageSize },
      );
      const options = mapReportRowsToOptions(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        reportResponse.results as any[],
        custom_report_label_path,
        custom_report_value_path,
      );
      const hasMore =
        reportResponse.page < Math.ceil(reportResponse.totalResults / pageSize);
      return { options, hasMore, additional: page + 1 };
    }

    const choicesResponse = await apiRequestContractHandler(
      getSocietyChoicesQuestionNameContract,
      {
        params: {
          societyId: props.session.societyId ?? 0,
          questionName: pillar_choices_question_name,
          ...(distinct_search_key !== undefined && {
            distinctOn: distinct_search_key,
          }),
        },
      },
      { text: inputValue },
      {
        page,
        pageSize,
      },
    );

    const options = choicesResponse.results.map((choices) => {
      const value = question_value
        ? choices[question_value as keyof SocietyCustomChoiceDto]
        : choices.customQuestionChoiceId;

      return {
        value: String(value),
        label: choices.text,
      };
    });
    const hasMore =
      choicesResponse.page < Math.ceil(choicesResponse.totalResults / pageSize);

    return {
      options,
      hasMore,
      additional: page + 1,
    };
  };

  const handleBarTemplate = (
    <TemplateComponent
      template={removeHTMLTags(handlebars_template)}
      data={{
        value: props.question.value || "",
        text: props.question.value || "",
      }}
    />
  );

  if (
    multi_select &&
    Array.isArray(props.question.value) &&
    min_choices > props.question.value?.length &&
    props.question.getType() === CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN
  ) {
    const expressionValidator = new ExpressionValidator();
    expressionValidator.expression = `{${props.question.name}}.length >= ${min_choices}`;
    expressionValidator.text = `You must select at least ${min_choices} choices.`;
    props.question.validators = [expressionValidator];
  } else {
    props.question.validators = [];
  }

  const handleChange = (value: any) => {
    // Whoever is filling the form has taken the question over, so a lookup
    // configured `doNotChangeValue` must not write over what they picked.
    props.question.valueSetByLookup = false;

    if (max_choices && Array.isArray(value) && value.length > max_choices) {
      value.pop();
    }

    if (!multi_select) {
      props.question.value = value.label;
      return;
    }

    props.question.value = value.map((v: OptionsValuesPillarChoice) => {
      return v.label;
    });
  };

  survey.onComplete.add((sender) => {
    const question = sender.getQuestionByName(props.question.name);

    // Only a standalone dropdown is normalised here. Inside a composite the
    // question of this name is the outer one, whose value is the whole content
    // panel - overwriting that with a bare value drops what the panel holds,
    // and the panel already stores exactly what this dropdown submitted.
    if (
      !question ||
      question.getType() !== CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN
    ) {
      return;
    }

    if (multi_select) {
      question.value = Array.isArray(question.value) ? question.value : [];
    } else {
      question.value =
        question.value && typeof question.value === "object"
          ? (question.value[question_value] ?? question.value)
          : question.value;
    }
  });

  const CreatebleAsyncPaginate = useMemo(
    () => withAsyncPaginate(Creatable) as AsyncPaginateCreatableType,
    [Creatable, withAsyncPaginate],
  );

  const getInitialValue = (value: any, isMultiSelect: any) => {
    if (!isMultiSelect) {
      if (!value) {
        return null;
      }
      const isObject = typeof value === "object";

      return {
        value: isObject ? value.value : value,
        label:
          handlebars_template !== undefined
            ? removeHTMLTags(renderToString(handleBarTemplate))
            : isObject
              ? value.label
              : value,
      };
    }

    if (Array.isArray(value)) {
      return value.map((val) => ({
        value: val,
        label:
          handlebars_template !== undefined
            ? removeHTMLTags(renderToString(handleBarTemplate))
            : val,
      }));
    }

    return [];
  };

  const isOptionAlreadySelected = (
    option: OptionsValuesPillarChoice,
    questionValue: unknown,
  ): boolean => {
    if (multi_select_duplicate) {
      return true;
    }

    const selected = Array.isArray(questionValue) ? questionValue : [];
    return !selected.includes(option.label);
  };

  return (
    <CreatebleAsyncPaginate
      isValidNewOption={(value: string) => value.length > 1 && other_option}
      loadOptions={fetchCustomChoices}
      filterOption={(option: OptionsValuesPillarChoice) =>
        isOptionAlreadySelected(option, props.question.value)
      }
      debounceTimeout={500}
      isMulti={multi_select}
      isDisabled={props.question.isReadOnly}
      placeholder={removeHTMLTags(placeholder_text) ?? "Select..."}
      value={getInitialValue(props.question.value, multi_select)}
      onChange={handleChange}
      styles={{
        option: (provided: StylesConfig) =>
          multi_select
            ? provided
            : {
                ...provided,
                backgroundColor: "primary-100",
                color: "black",
              },
      }}
    />
  );
};

export const RegisterPillarChoiceDropdownComponent = (
  session: SessionView,
  creator?: SurveyCreatorModel,
) => {
  ElementFactory.Instance.registerElement(
    CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN,
    (name) => {
      const question = new PillarChoiceDropdownModel(name);

      question.session = session;
      question.clearIfInvisible = "none";

      return question;
    },
  );

  Serializer.addClass(
    CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN,
    [{ name: "height", default: "200px", category: "layout" }],
    function () {
      const question = new PillarChoiceDropdownModel("");

      question.session = session;
      question.clearIfInvisible = "none";

      return question;
    },
    "question",
  );

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "pillar_choices_question_name",
    displayName: "Pillar Choice Category",
    type: "dropdown",
    category: "general",
    // The full category list is loaded lazily when the admin opens this
    // dropdown (see wirePillarChoiceCategoryLazyLoad). Up front we only surface
    // the custom report option and the currently-selected category so the
    // editor renders its value without querying for every selected question.
    choices: (obj: Question) => {
      const current = obj?.getPropertyValue("pillar_choices_question_name") as
        | string
        | undefined;

      const choices: PillarChoiceCategoryOption[] = [
        customReportCategoryOption,
      ];
      if (current && current !== PILLAR_CHOICE_CUSTOM_REPORT_VALUE) {
        choices.push({ value: current, text: current });
      }
      return choices;
    },
  });

  if (creator) {
    wirePillarChoiceCategoryLazyLoad(creator, session);
  }

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "placeholder_text",
    displayName: "Placeholder text",
    type: "text",
    category: "general",
  });

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "link_question_values",
    displayName: "Link Question Values",
    type: "string",
    category: "Linked Questions",
  });

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "doNotChangeValue",
    displayName: "Only populate while empty",
    type: "boolean",
    default: false,
    category: "Linked Questions",
  });

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "doNotSendIfClick",
    displayName: "Ignore filter on dropdown click",
    type: "boolean",
    default: false,
    category: "Custom Report",
    visibleIf: function (obj: Question) {
      return !!obj.custom_report_id;
    },
  });

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "question_value",
    displayName: "Question Value",
    type: "dropdown",
    choices: [
      {
        value: "label",
        text: "Text",
      },
      {
        value: "value",
        text: "Value",
      },
    ],
    category: "Linked Questions",
    visibleIf: function (obj: Question) {
      return !!obj.link_question_values;
    },
  });

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "handlebars_template",
    displayName: "Handlebars Template",
    type: "text",
    category: "Linked Questions",
    visibleIf: function (obj: Question) {
      return !!obj.link_question_values;
    },
  });

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "multi_select",
    displayName: "Multi select",
    type: "boolean",
    default: false,
    category: "general",
  });

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "multi_select_duplicate",
    displayName: "Enable multi select duplicate",
    default: false,
    type: "boolean",
    category: "general",
  });

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "min_choices",
    displayName: "Minimum choices for multi select",
    type: "number",
    category: "general",
    visibleIf: function (obj: Question) {
      return obj.multi_select === true;
    },
  });

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "max_choices",
    displayName: "Maximum choices for multi select",
    type: "number",
    category: "general",
    visibleIf: function (obj: Question) {
      return obj.multi_select === true;
    },
  });

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "other_option",
    displayName: "Other option",
    type: "boolean",
    default: false,
    category: "general",
  });

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "distinct_search_key",
    displayName: "Distinct search key",
    type: "dropdown",
    choices: [
      { value: "text", text: "Text" },
      { value: "value", text: "Value" },
    ],
    category: "Linked Questions",
    visibleIf: function (obj: Question) {
      return !!obj.link_question_values;
    },
  });

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "custom_report_id",
    displayName: "Custom Report ID",
    type: "number",
    category: "Custom Report",
    visibleIf: function (obj: Question) {
      // Shown when the custom report option is selected, and also kept visible
      // for forms configured before that option existed (custom_report_id was
      // set directly), so those remain editable in the builder.
      return (
        obj.pillar_choices_question_name ===
          PILLAR_CHOICE_CUSTOM_REPORT_VALUE || !!obj.custom_report_id
      );
    },
  });

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "custom_report_label_path",
    displayName: "Label Path",
    type: "string",
    category: "Custom Report",
    visibleIf: function (obj: Question) {
      return !!obj.custom_report_id;
    },
  });

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "custom_report_value_path",
    displayName: "Value Path",
    type: "string",
    category: "Custom Report",
    visibleIf: function (obj: Question) {
      return !!obj.custom_report_id;
    },
  });

  Serializer.addClass(
    "pillarchoicereportfilter",
    [
      { name: "filterKey", type: "string", isRequired: true },
      { name: "questionName", type: "string", isRequired: true },
    ],
    undefined,
    "itemvalue",
  );

  Serializer.addProperty(CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN, {
    name: "custom_report_filters",
    displayName: "Filter Options",
    type: "pillarchoicereportfilter[]",
    category: "Custom Report",
    visibleIf: function (obj: Question) {
      return !!obj.custom_report_id;
    },
  });

  ReactQuestionFactory.Instance.registerQuestion(
    CUSTOM_TYPE_PILLAR_CHOICE_DROPDOWN,
    (props: any) => {
      return createElement(PillarChoiceDropdown, {
        ...props,
        session,
      });
    },
  );
};
